import {
  Box,
  Button,
  Card,
  CoachTip,
  Divider,
  Flex,
  Heading,
  Inline,
  Loader,
  Stack,
  Text,
} from "@compono/ui";
import {
  QuestionMarkCircleOutlineIcon,
  TriangleDotSolidIcon,
} from "@compono/ui-icons";
import React, { useState } from "react";
import { CulturePerceptionResult } from "~/lib/federationGateway/graphql";
import BarStackChart from "../../../components/BarStackChart/BarStackChart";
import {
  ATMOSPHERE_DIMENSIONS,
  ATMOSPHERE_HEADING,
  COMPANY_DIMENSIONS,
  COMPANY_HEADING,
  engagementLabel,
  engagementQuestion,
  OVERALL,
  satisfactionRating,
} from "../engagementCopy";
import EngagementItem from "./EngagementItem";
import { HowToDialog } from "./HowToDialog";
import { buildHoverContent, buildItems, emptyBands } from "./utils";

/** An even bar, for the overall card of a column whose responses have not arrived. */
const evenBands = () => emptyBands().map((band) => ({ ...band, value: 1 }));

/**
 * One column's questions, each with how it was worded and how it was answered.
 *
 * The wording comes from `engagementCopy` rather than being written out here, so this screen and
 * the culture report cannot end up asking the reader two different questions about one score.
 */
const QuestionItems = ({
  dimensions,
  culturePerception,
}: {
  dimensions: readonly string[];
  culturePerception: CulturePerceptionResult;
}) => (
  <>
    {dimensions.map((dimension, index) => (
      <React.Fragment key={dimension}>
        {index > 0 && <Divider orientation={"horizontal"} />}
        <EngagementItem
          title={engagementLabel(dimension)}
          description={engagementQuestion(dimension)}
          distribution={culturePerception?.perceptionList.map(
            (perception: Record<string, number>) => perception[dimension],
          )}
          average={culturePerception?.perceptionAverage?.[dimension]}
        />
      </React.Fragment>
    ))}
  </>
);

const HeadingBox = ({
  title,
  average,
  items = evenBands(),
}: {
  title: string;
  average: number;
  items: any;
}) => {
  return (
    <>
      <Text size="lg" sx={{ textAlign: "center", width: "100%", pt: 5, mb: 5 }}>
        <b>{title}</b>
      </Text>
      <Inline alignX="center" gap="1">
        <Text size="xxl">
          <b>{average} </b>
        </Text>
        <Text size="md" sx={{ color: "black.50" }}>
          <b> / 5</b>
        </Text>
      </Inline>
      <Box sx={{ p: 5 }}>
        <Card
          sx={{
            backgroundColor: "black.5",
            width: "100%",
            p: 5,
          }}
        >
          <Stack>
            <Text size="sm" sx={{ color: "black.50", position: "absolute" }}>
              {OVERALL}
            </Text>
            <Text size="xl" sx={{ textAlign: "center", width: "100%", pb: 3 }}>
              {satisfactionRating(average)}
            </Text>

            <Box sx={{ width: "100%" }}>
              <BarStackChart
                items={items}
                markerValue={average}
              ></BarStackChart>
            </Box>
          </Stack>
        </Card>
      </Box>
    </>
  );
};

const EngagementContent = ({
  loading,
  culturePerception,
}: {
  loading: boolean;
  culturePerception: CulturePerceptionResult;
}) => {
  const [howToOpen, setHowToOpen] = useState<boolean>(false);

  if (loading) {
    return (
      <Box sx={{ margin: "20px" }}>
        <Inline alignX="center">
          <Loader size="lg" label="loading" hideLabel />
        </Inline>
      </Box>
    );
  }

  const header = `Showing ${culturePerception?.completedUsersCount} of
    ${culturePerception?.totalUsersCompleted} responses (based on filters applied)`;

  // The atmosphere questions are only answered by groups of three or more, so one of their
  // averages is what says whether that column has anything to show at all.
  const { jobEngagement } = culturePerception?.perceptionAverage || {};

  let companyAverage = 0;
  let atmosphereAverage = 0;
  let companyItems = undefined;
  let atmosphereItems = undefined;
  if (culturePerception?.perceptionList) {
    const {
      companySum,
      companyItems: accCompanyItems,
      atmosphereSum,
      atmosphereItems: accAtmosphereItems,
    } = culturePerception?.perceptionList?.reduce(
      (accumulator, currentValue) => {
        accumulator.companySum =
          accumulator.companySum +
          currentValue?.financial +
          currentValue?.operational +
          currentValue?.learningGrowth +
          currentValue?.customerQuality;
        accumulator.atmosphereSum =
          accumulator.atmosphereSum +
          currentValue?.jobEngagement +
          currentValue?.jobSatisfaction +
          currentValue?.turnoverIntention +
          currentValue?.workMotivation +
          currentValue?.overallClimate;

        accumulator.companyItems = buildItems(
          accumulator.companyItems,
          currentValue?.financial,
        );
        accumulator.companyItems = buildItems(
          accumulator.companyItems,
          currentValue?.operational,
        );
        accumulator.companyItems = buildItems(
          accumulator.companyItems,
          currentValue?.learningGrowth,
        );
        accumulator.companyItems = buildItems(
          accumulator.companyItems,
          currentValue?.customerQuality,
        );

        accumulator.atmosphereItems = buildItems(
          accumulator.atmosphereItems,
          currentValue?.jobEngagement,
        );

        accumulator.atmosphereItems = buildItems(
          accumulator.atmosphereItems,
          currentValue?.jobSatisfaction,
        );
        accumulator.atmosphereItems = buildItems(
          accumulator.atmosphereItems,
          currentValue?.turnoverIntention,
        );
        accumulator.atmosphereItems = buildItems(
          accumulator.atmosphereItems,
          currentValue?.workMotivation,
        );
        accumulator.atmosphereItems = buildItems(
          accumulator.atmosphereItems,
          currentValue?.overallClimate,
        );
        return accumulator;
      },
      {
        companySum: 0,
        companyItems: emptyBands(),
        atmosphereSum: 0,
        atmosphereItems: emptyBands(),
      },
    );
    companyAverage =
      Math.round(
        (companySum / (4 * culturePerception?.perceptionList.length)) * 10,
      ) / 10;
    atmosphereAverage =
      Math.round(
        (atmosphereSum / (5 * culturePerception?.perceptionList.length)) * 10,
      ) / 10;
    companyItems = accCompanyItems.map((item) => {
      const content = buildHoverContent(
        item,
        culturePerception?.perceptionList.length * 4,
        false,
      );
      return {
        ...item,
        content,
      };
    });
    atmosphereItems = accAtmosphereItems.map((item) => {
      const content = buildHoverContent(
        item,
        culturePerception?.perceptionList.length * 5,
        false,
      );
      return {
        ...item,
        content,
      };
    });
  }

  return (
    <>
      <Box
        sx={{
          ml: "10px",
          mr: "10px",
        }}
      >
        <Card border sx={{ m: 3, p: 0, maxWidth: "1440px" }}>
          <Box sx={{ m: 5 }}>
            <Inline alignX="apart">
              <Stack gap={1}>
                <Heading level="5">Overview</Heading>
                <Text size="sm">{header}</Text>
              </Stack>
              <Button
                look="naked"
                iconLeft={QuestionMarkCircleOutlineIcon}
                onClick={() => {
                  setHowToOpen(true);
                }}
              >
                How does this work?
              </Button>
            </Inline>
          </Box>
          <Stack>
            <Divider orientation={"horizontal"} />
            <Box sx={{ pt: 5, pb: 5, minHeight: "55px" }}>
              <Box sx={{ ml: 5, position: "absolute" }}>
                <Inline>
                  <TriangleDotSolidIcon />
                  <Text>Average score</Text>
                </Inline>
              </Box>
              {/* <Inline alignX="center">
                    <Box>
                      <ButtonGroup
                        rounded
                        defaultSelected="default-button"
                        name="rounded-group"
                      >
                        <Button id="default-button">Distribution</Button>
                        <Button>Radar chart</Button>
                      </ButtonGroup>
                    </Box>
                  </Inline> */}
            </Box>
            <Divider orientation={"horizontal"} />
            <Flex>
              <Stack
                gap={2}
                sx={{
                  borderRight: "1px solid",
                  borderColor: "black.10",
                }}
              >
                <HeadingBox
                  title={COMPANY_HEADING}
                  average={companyAverage}
                  items={companyItems}
                />
                <Box
                  sx={{
                    height: "100%",
                    padding: "20px",
                  }}
                >
                  <Divider orientation={"horizontal"} />
                  <Stack gap={4}>
                    <QuestionItems
                      dimensions={COMPANY_DIMENSIONS}
                      culturePerception={culturePerception}
                    />
                  </Stack>
                </Box>
              </Stack>
              <Stack gap={2}>
                {jobEngagement ? (
                  <>
                    <HeadingBox
                      title={ATMOSPHERE_HEADING}
                      average={atmosphereAverage}
                      items={atmosphereItems}
                    />

                    <Card sx={{ padding: "20px" }}>
                      <Divider orientation={"horizontal"} />
                      <Stack gap={4}>
                        <QuestionItems
                          dimensions={ATMOSPHERE_DIMENSIONS}
                          culturePerception={culturePerception}
                        />
                      </Stack>
                    </Card>
                  </>
                ) : (
                  <Box sx={{ pl: 5, pr: 5 }}>
                    <Text
                      size="lg"
                      sx={{
                        textAlign: "center",
                        width: "100%",
                        pt: 5,
                        pb: 5,
                      }}
                    >
                      <b>{ATMOSPHERE_HEADING}</b>
                    </Text>
                    <CoachTip
                      look="warning"
                      title="Not enough responses to show"
                      sx={{ mt: 9 }}
                    >
                      <Text>
                        Work atmosphere results are sensitive. Results will only
                        be displayed for groups of 3 or more responses.
                      </Text>
                    </CoachTip>
                  </Box>
                )}
              </Stack>
            </Flex>
          </Stack>
        </Card>
      </Box>

      <HowToDialog
        howToOpen={howToOpen}
        setHowToOpen={setHowToOpen}
      ></HowToDialog>
    </>
  );
};
export default EngagementContent;
